import { h } from "preact";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { TreeExplorer, TreeNode, TreeStructure } from "./TreeExplorer";
import { DynamicDirectoryInput } from "./DynamicDirectoryInput";
import { DynamicFile, NativeDirectoryHandle, NativeFileHandle } from "./DynamicFile";
import { DynamicFileInput, DynamicWriteableFileInput } from "./DynamicFileInput";
import { FileInput } from "./FileInput";
import { useInputs, useCheckMemo, useCommandHandler, useDepRef, useValue, useInlineMemo } from "../hooks";
import { Command } from "../hooks/Command";
import { getLabelFiles, LabelData, loadLabels } from "../api/label-creator";

function recursiveFindFileByName(name: string, path: readonly string[], data: LabelData): { key: string, path: readonly string[], data: LabelData }[] {
    const output: { key: string, path: readonly string[], data: LabelData }[] = [];

    const parts = name.split(" ").filter(v => v);

    for (const prop in data) {
        const item = data[prop];

        if (item["/type"] === "file") {
            const title = item.metadata["display-name"] ? item.metadata["display-name"] : prop;

            if (parts.every(v => title.toLocaleLowerCase().indexOf(v.toLocaleLowerCase()) >= 0)) {
                output.push({ key: prop, path, data: item });
            }
        }

        if (item["/type"] === "directory") {
            output.push(...recursiveFindFileByName(name, path.concat(prop), item));
        }
    }

    return output;
}

async function recursiveCreateDirectoryData(directory: NativeDirectoryHandle): Promise<TreeStructure<NativeFileHandle>> {
    const result = {} as TreeNode<NativeFileHandle>;

    for await (let [name, handle] of directory.entries()) {
        if (!result.inner) {
            result.inner = {};
        }

        if (handle.kind === "file") {
            result.inner[name] = { value: handle };
        }
        else {
            result.inner[name] = await recursiveCreateDirectoryData(handle);
        }
    }

    return result;
}

export function LabelFilesExplorer(props: {
    onChange: (data: { path: string, metadata: { [key: string]: string }, file?: File | Blob } | null) => void,
    showOnlyEditorFileUpload?: boolean,
    onEditorFileChange?: (file: DynamicFile | null) => void,
    doSelectTemplateByPath?: Command<[path: string]>
    showAdvancedOptions?: boolean
    height?: number
    onPathChange?: (path: string) => void
    doReload?: Command,
    path?: string
}) {
    const [labelData, setLabelData] = useState(null as LabelData | null);
    const [labelDataReload, setLabelDataReload] = useState(0);
    const [currentPath, setCurrentPath] = useState(props.path?.split("/") ?? []);
    const [selectedItem, setSelectedItem] = useState(null as string | null);
    const [searchPath, setSearchPath] = useState("");
    const [directoryData, setDirectoryData] = useState({} as TreeStructure<NativeFileHandle>);
    const templateFileRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        getLabelFiles().then(v => setLabelData(v ?? {} as LabelData));
    }, [labelDataReload])

    useMemo(() => {
        props.onPathChange?.(currentPath.join("/"));
    }, [currentPath]);

    useMemo(() => {
        setCurrentPath(props.path ? props.path.split("/") : []);
    }, [props.path]);

    useCommandHandler(props.doSelectTemplateByPath, (path) => {
        const split = path.split("/");
        let data = labelData;

        if (!data) {
            return;
        }

        for (let i = 0; i < split.length; i++) {
            const part = split[i];
            data = data[part];

            if (!data) {
                return;
            }
        }

        if (data["/type"] !== "file") {
            return;
        }

        setCurrentPath(split.slice(0, split.length - 1));
        setSelectedItem(split[split.length - 1]);
        props.onChange({ path: split.join("/"), metadata: data.metadata });
    }); //, [labelData]);

    useCommandHandler(props.doReload,
        useInlineMemo([]) ?? useValue(() => { setLabelDataReload(prev => prev + 1); })
    );

    let currentLabelData = labelData;

    if (currentLabelData) {
        for (let i = 0; i < currentPath.length; i++) {
            currentLabelData = currentLabelData[currentPath[i]];
        }
    }

    return <div class="label-template-explorer">
        {!props.showOnlyEditorFileUpload ? <div class="explorer">
            <div class="search">
                <input type="search" placeholder="Wyszukaj..." value={searchPath} onInput={(e) => setSearchPath(e.currentTarget.value)}/>
            </div>
            <div class="menu">
                <div class="back" onClick={() => {
                    if (currentPath.length <= 0) {
                        return
                    }

                    setCurrentPath((prev) => prev.slice(0, prev.length - 1))
                    setSelectedItem(null);
                    props.onChange(null);
                }}>
                    <button type="button" class="icon flat borderless r26">arrow_upward</button>
                </div>
                <div class="path">
                    <span class="path-link" onClick={() => {
                        setCurrentPath([]);
                        setSelectedItem(null);
                        props.onChange(null);
                    }}>Naklejki</span>
                    {currentPath.map((p, i) => [
                        <span class="icon">chevron_right</span>,
                        <span class="path-link" onClick={() => {
                            setCurrentPath(currentPath.slice(0, i + 1));
                            setSelectedItem(null);
                            props.onChange(null);
                        }}>{p}</span>
                    ])}
                </div>
            </div>
            <div class="list success" style={{ height: props.height ? props.height + "px" : "", overflow: "auto" }}>
                {searchPath && currentLabelData ?
                    recursiveFindFileByName(searchPath, currentPath, currentLabelData).map(value => {
                        if (value.key.startsWith("/"))  {
                            return;
                        }

                        let relativePath = value.path.filter((v, i) => v !== currentPath[i]).join("/");

                        if (relativePath) {
                            relativePath += "/";
                        }

                        return <LabelTemplatesExplorerEntry
                            class={"item" + (selectedItem === value.key ? " selected" : "") + (value.data["/type"] === "directory" ? " directory" : " file")}
                            title={relativePath + (value.data["/type"] === "file" ? (value.data.metadata["display-name"] ? value.data.metadata["display-name"] : value.key) : value.key)}
                            data={value.data}
                            onClick={(data) => {
                                if (data["/type"] === "directory") {
                                    setCurrentPath((prev) => prev.concat([value.key]));
                                }
                                else {
                                    setSelectedItem(value.key);
                                    props.onChange({ path: value.path.join("/") + "/" + value.key, metadata: data.metadata });
                                }
                            }}/>;
                    })
                : currentLabelData ? Object.entries(currentLabelData).map(([key, value]) => {
                    if (key.startsWith("/") || typeof value === "string")  {
                        return;
                    }

                    return <LabelTemplatesExplorerEntry
                        class={"item" + (selectedItem === key ? " selected" : "") + (value["/type"] === "directory" ? " directory" : " file")}
                        title={value["/type"] === "file" ? (value.metadata["display-name"] ? value.metadata["display-name"] : key) : key}
                        data={value}
                        onClick={(data) => {
                            if (data["/type"] === "directory") {
                                setCurrentPath((prev) => prev.concat([key]));
                            }
                            else {
                                setSelectedItem(key);
                                props.onChange({ path: currentPath.join("/") + "/" + key, metadata: data.metadata });
                            }
                        }}/>;
                }) : null}
            </div>
            <div class="accordion" style={{ borderTop: "1px solid rgba(255, 255, 255, 0.1)", display: props.showAdvancedOptions ? "" : "none" }}>
                <div>
                    <input type="radio" name="accordion-label-tree-view"/>
                    <input type="radio" name="accordion-label-tree-view"/>
                    <div class="header" style="padding-left: 10px;">Zaawansowane</div>
                    <div class="content" style="padding: 10px; flex-direction: column;">

                    </div>
                    <div class="content" style="padding: 10px; flex-direction: column;">
                        <div>
                            Załaduj szablon: {DynamicFile.isSupported ? <DynamicFileInput
                                types={[{ accept: { "image/svg+xml": ".svg" }}]}
                                onChange={async (files) => {
                                    if (files.length === 0) {
                                        setLabelDataReload(prev => prev + 1);
                                        setCurrentPath([]);
                                        setSelectedItem(null);
                                        props.onChange(null);
                                    }
                                    else {
                                        setCurrentPath([]);
                                        setSelectedItem(null);
                                        props.onChange(null);

                                        const formData = new FormData();
                                        const file = new Blob([await files[0].arrayBuffer()], { type: "image/svg+xml" });
                                        formData.append("template", file);

                                        loadLabels(formData).then(async (response) => {
                                            if (!response.ok) {
                                                return;
                                            }

                                            const result = await response.json();
                                            setLabelData(result);

                                            const key = Object.keys(result)[0];
                                            setCurrentPath([]);
                                            setSelectedItem(key);
                                            props.onChange({ file, path: "/" + key, metadata: result[key].metadata });
                                        });
                                    }
                                }}/> : [<input ref={templateFileRef} accept=".svg" type="file" onChange={async (e) => {
                                    if (!e.currentTarget.files || e.currentTarget.files.length === 0) {
                                        setLabelDataReload(prev => prev + 1);
                                        setCurrentPath([]);
                                        setSelectedItem(null);
                                        props.onChange(null);
                                    }
                                    else {
                                        const formData = new FormData();
                                        const file = new Blob([await e.currentTarget.files[0].arrayBuffer()], { type: "image/svg+xml" });
                                        formData.append("template", file);

                                        loadLabels(formData).then(async (response) => {
                                            if (!response.ok) {
                                                return;
                                            }

                                            const result = await response.json();
                                            setLabelData(result);

                                            const key = Object.keys(result)[0];
                                            setCurrentPath([]);
                                            setSelectedItem(key);
                                            props.onChange({ file, path: "/" + key, metadata: result[key].metadata });
                                        });
                                    }
                                }}></input>, <button type="button" class="error" onClick={() => {
                                templateFileRef.current!.value = "";

                                setLabelDataReload(prev => prev + 1);
                                setCurrentPath([]);
                                setSelectedItem(null);
                                props.onChange(null);
                            }}>Wyczyść</button>]}
                        </div>
                    </div>
                </div>
            </div>
        </div> : <div class="explorer">
            <div>
                Załaduj szablon: {DynamicFile.isSupported ? <DynamicWriteableFileInput
                    types={[{ accept: { "image/svg+xml": ".svg" }}]}
                    onChange={async (file) => {
                        if (!file) {
                            setLabelDataReload(prev => prev + 1);
                            setCurrentPath([]);
                            setSelectedItem(null);
                            props.onChange(null);

                            if (props.onEditorFileChange) {
                                props.onEditorFileChange(null);
                            }
                        }
                        else {
                            const formData = new FormData();
                            const fileData = await file.readFile();
                            formData.append("template", fileData);

                            if (props.onEditorFileChange) {
                                props.onEditorFileChange(file);
                            }

                            loadLabels(formData).then(async (response) => {
                                if (!response.ok) {
                                    return;
                                }

                                const result = await response.json();
                                setLabelData(result);

                                const key = Object.keys(result)[0];
                                setCurrentPath([]);
                                setSelectedItem(key);
                                props.onChange({ file: fileData, path: currentPath.join("/") + "/" + key, metadata: result[key].metadata });
                            });
                        }
                    }}/> : [<input ref={templateFileRef} type="file" onChange={(e) => {
                        if (!e.currentTarget.files || e.currentTarget.files.length === 0) {
                            setLabelDataReload(prev => prev + 1);
                            setCurrentPath([]);
                            setSelectedItem(null);
                            props.onChange(null);
                        }
                        else {
                            const formData = new FormData();
                            const file = e.currentTarget.files[0];
                            formData.append("template", file);

                            loadLabels(formData).then(async (response) => {
                                if (!response.ok) {
                                    return;
                                }

                                const result = await response.json();
                                setLabelData(result);

                                const key = Object.keys(result)[0];
                                setCurrentPath([]);
                                setSelectedItem(key);
                                props.onChange({ file, path: currentPath.join("/") + "/" + key, metadata: result[key].metadata });
                            });
                        }
                    }}></input>, <button type="button" class="error" onClick={() => {
                    templateFileRef.current!.value = "";

                    setLabelDataReload(prev => prev + 1);
                    setCurrentPath([]);
                    setSelectedItem(null);
                    props.onChange(null);
                }}>Wyczyść</button>]}
            </div>
            <div>
                <DynamicDirectoryInput onChange={async (directory) => {
                    if (!directory) {
                        setDirectoryData({});
                        return;
                    }

                    setDirectoryData(await recursiveCreateDirectoryData(directory));
                }} />
                <TreeExplorer class="editor-folder-explorer" rootName="Wybrany folder" onChange={async (data) => {
                    if (data?.value) {
                        setCurrentPath([]);
                        setSelectedItem(null);
                        props.onChange(null);

                        if (props.onEditorFileChange) {
                            props.onEditorFileChange(null);
                        }

                        const file = new DynamicFile(data.value);
                        const formData = new FormData();
                        const fileData = await file.readFile();
                        formData.append("template", fileData);


                        loadLabels(formData).then(async (response) => {
                            if (!response.ok) {
                                return;
                            }

                            const result = await response.json();
                            setLabelData(result);

                            const key = Object.keys(result)[0];
                            setCurrentPath([]);
                            setSelectedItem(key);
                            props.onChange({ file: fileData, path: currentPath.join("/") + "/" + key, metadata: result[key].metadata });

                            if (props.onEditorFileChange) {
                                props.onEditorFileChange(file);
                            }
                        });

                        return;
                    }
                }} treeData={directoryData}/>
            </div>
        </div>}
    </div>;
}

function LabelTemplatesExplorerEntry(props: { class: string, title: string, data: LabelData, onClick: (data: LabelData) => void }) {
    // console.log(props.data);
    return <div class={props.class} onClick={() => props.onClick(props.data)}>
        <div>{props.title}</div>
        <div style="margin-left: 10px">
        </div>
    </div>;
}
